"use client";

import { SafeHydrate } from "@/SafeHydrate";
import React from "react";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Loader, Calendar, Users, Sparkles } from "lucide-react";
function BookingForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="flex flex-col items-center justify-center py-12 text-center space-y-6 animate-in fade-in duration-700">
        <div className="size-16 rounded-full bg-secondary/20 flex items-center justify-center mb-4">
          <Sparkles className="size-8 text-secondary" />
        </div>
        <h3 className="font-default text-3xl text-foreground">Journey Confirmed</h3>
        <p className="text-muted-foreground font-sans max-w-md">
          {successMessage}
        </p>
        <Button onClick={resetForm} variant="outline" className="mt-8 border-secondary text-secondary hover:bg-secondary hover:text-secondary-foreground rounded-sm">
          Plan Another Experience
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-6 w-full max-w-md mx-auto lg:mx-0 relative z-20">
      <div className="space-y-4">
        <FormField control={control} name="fullName" rules={{
        required: "Please enter your full name"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="font-sans text-foreground/80 uppercase tracking-wider text-xs">Full Name</FormLabel>
              <FormControl>
                <Input placeholder="Jane Doe" className="bg-foreground text-background border-border focus-visible:border-secondary focus-visible:ring-secondary rounded-sm h-12 px-4 placeholder:text-background/40" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="email" rules={{
        required: "Email is required",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Please enter a valid email address"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="font-sans text-foreground/80 uppercase tracking-wider text-xs">Email Address</FormLabel>
              <FormControl>
                <Input type="email" placeholder="jane@example.com" className="bg-foreground text-background border-border focus-visible:border-secondary focus-visible:ring-secondary rounded-sm h-12 px-4 placeholder:text-background/40" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <FormField control={control} name="date" rules={{
          required: "Preferred date is required"
        }} render={({
          field
        }) => <FormItem>
                <FormLabel className="font-sans text-foreground/80 uppercase tracking-wider text-xs flex items-center gap-2">
                  <SafeHydrate><Calendar className="size-3" /></SafeHydrate> Preferred Date
                </FormLabel>
                <FormControl>
                  <Input type="date" className="bg-foreground text-background border-border focus-visible:border-secondary focus-visible:ring-secondary rounded-sm h-12 px-4 [color-scheme:dark]" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>} />

          <FormField control={control} name="guests" rules={{
          required: "Number of guests is required",
          min: {
            value: 1,
            message: "Minimum 1 guest"
          }
        }} render={({
          field
        }) => <FormItem>
                <FormLabel className="font-sans text-foreground/80 uppercase tracking-wider text-xs flex items-center gap-2">
                  <Users className="size-3" /> Guests
                </FormLabel>
                <FormControl>
                  <Input type="number" min="1" placeholder="2" className="bg-foreground text-background border-border focus-visible:border-secondary focus-visible:ring-secondary rounded-sm h-12 px-4 placeholder:text-background/40" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>} />
        </div>

        <FormField control={control} name="requests" render={({
        field
      }) => <FormItem>
              <FormLabel className="font-sans text-foreground/80 uppercase tracking-wider text-xs">Special Requests (Optional)</FormLabel>
              <FormControl>
                <textarea className="flex w-full min-w-0 bg-foreground text-background border-border focus-visible:border-secondary focus-visible:ring-secondary focus-visible:ring-[3px] rounded-sm border px-4 py-3 text-base shadow-xs transition-[color,box-shadow] outline-none disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50 md:text-sm placeholder:text-background/40 min-h-[100px] resize-y" placeholder="Dietary requirements, celebrations..." {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      {submitError && <div className="p-4 bg-destructive/10 border border-destructive/20 rounded-sm">
          <p className="text-destructive text-sm font-sans">{submitError}</p>
        </div>}

      <Button type="submit" disabled={isSubmitting} className="w-full h-14 bg-primary text-primary-foreground hover:bg-primary/90 rounded-sm text-base tracking-wide uppercase font-sans font-semibold transition-all duration-300">
        {isSubmitting ? <>
            <Loader className="animate-spin mr-2 size-5" />
            Reserving...
          </> : "Submit Request"}
      </Button>
    </Form>;
}
export default function BookYourLuxuryExperience() {
  return <section id="book-your-luxury-experience" className="bg-background relative overflow-hidden min-h-screen flex flex-col">
      <div className="grid grid-cols-1 lg:grid-cols-12 flex-grow">
        
        {}
        <div className="lg:col-span-7 relative h-[50vh] lg:h-auto w-full overflow-hidden">
          <div className="absolute inset-0 bg-foreground/10 z-10 mix-blend-overlay pointer-events-none"></div>
          <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1634877/img/colombian_emeralds_macro.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1634877/img/colombian_emeralds_macro.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1634877/img/colombian_emeralds_macro.png-wvc-sizes" alt="Immersive close-up of Colombian emeralds" className="w-full h-full object-cover saturate-50 scale-105 transition-transform duration-1000 hover:scale-100" loading="lazy" />
        </div>

        {}
        <div className="lg:col-span-5 relative flex flex-col justify-center p-8 md:p-12 lg:p-16 xl:p-24 bg-background">
          
          {}
          <div className="mb-12 lg:mb-16 lg:absolute lg:top-32 lg:-left-32 xl:-left-48 z-30 pointer-events-none">
            <h2 className="font-default text-5xl md:text-6xl lg:text-7xl xl:text-8xl text-secondary leading-none drop-shadow-lg">
              Reserve<br className="hidden lg:block" /> Your<br className="hidden lg:block" /> Journey
            </h2>
          </div>

          <div className="relative z-20 w-full">
            <p className="font-sans text-muted-foreground mb-8 max-w-md text-sm md:text-base leading-relaxed">
              Begin your bespoke curation process. Share your preferences below, and our specialists will craft an itinerary tailored to your exact desires.
            </p>

            <FormProvider formId="luxury-booking-form" sectionName="Book Your Luxury Experience" defaultValues={{
            fullName: "",
            email: "",
            date: "",
            guests: "",
            requests: ""
          }} submitText="Submit Request" successMessage="Your journey request has been received. A curation specialist will contact you within 24 hours.">
              <BookingForm />
            </FormProvider>
          </div>
          
          {}
          <div className="absolute bottom-0 right-0 w-64 h-64 bg-secondary/5 rounded-tl-full pointer-events-none blur-3xl"></div>
        </div>

      </div>
    </section>;
}